import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ACCESS_TOKEN, isLocalSetupValid } from "../../constants";
import { identityExchange } from "./useLogin";
import { isPathPublicViewer } from "Utilities/generalUtilities";
import { store } from "../../store";
import { RootState } from "reducers";

export const WHITELISTED_ORIGINS = ["*.curbiq.development.dev.hotspotparking.us"];

type IdentityExchangeState = {
    isChecking: boolean;
    isAuthenticated: boolean;
    error?: string;
    errorCode?: number;
};

export const postMessageToParent = (data: Record<string, unknown>): void => {
    const isIframeView = store.getState().city.isIframeView === true;
    if (isIframeView && window.parent) {
        window.parent.postMessage({ source: "curbiq", ...data }, "*");
    }
};

export const useIdentityExchange = () => {
    // isIframeView: true when CurbIQ is loaded inside an iframe (not on CurbIQ domain)
    const isIframeView = useSelector((state: RootState) => state.city.isIframeView);

    const [state, setState] = useState<IdentityExchangeState>({
        isChecking: true,
        isAuthenticated: false
    });

    useEffect(() => {
        if (isLocalSetupValid()) {
            setState({ isChecking: false, isAuthenticated: true });
            return;
        }

        // Wait until the currentCity saga has set isIframeView (non-null)
        if (isIframeView === null) return;

        const executeIdentityExchange = async () => {
            // 1. Skip for PFCV routes — no auth required
            if (isPathPublicViewer()) {
                setState({ isChecking: false, isAuthenticated: false });
                return;
            }

            // ──────────────────────────────────────────────
            // Direct CurbIQ access → skip identity exchange entirely
            // Use existing routing (login page, refresh token, etc.)
            // ──────────────────────────────────────────────
            if (!isIframeView) {
                const existingToken = localStorage.getItem(ACCESS_TOKEN);
                setState({
                    isChecking: false,
                    isAuthenticated: !!existingToken
                });
                return;
            }

            // ──────────────────────────────────────────────
            // Iframe view → call identity exchange to get accessToken
            // ──────────────────────────────────────────────
            try {
                const response = await identityExchange();

                if (response.accessToken) {
                    localStorage.setItem(ACCESS_TOKEN, response.accessToken);
                    localStorage.setItem("isUserAuthenticated", "true");
                    localStorage.setItem("lastAPICall", JSON.stringify(Date.now()));

                    postMessageToParent({
                        type: "AUTH_SUCCESS",
                        message: "User authenticated successfully"
                    });

                    setState({ isChecking: false, isAuthenticated: true });
                    return;
                }

                // External + no token — authentication failed
                postMessageToParent({
                    type: "AUTH_ERROR",
                    errorCode: response.errorCode,
                    error: response.error
                });

                setState({
                    isChecking: false,
                    isAuthenticated: false,
                    error: response.error,
                    errorCode: response.errorCode
                });
            } catch (error: unknown) {
                const errorMessage =
                    error instanceof Error ? error.message : "Identity exchange failed";

                postMessageToParent({
                    type: "AUTH_ERROR",
                    errorCode: 2500,
                    error: errorMessage
                });

                setState({
                    isChecking: false,
                    isAuthenticated: false,
                    error: errorMessage,
                    errorCode: 2500
                });
            }
        };

        executeIdentityExchange();
    }, [isIframeView]);

    return { ...state, isIframeView };
};
